import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { Alert, Button, InputNumber, Tag } from 'antd'
import { GiftOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import i from '../../../translations/i'
import { openApi } from '../../../helpers/request'
import { storeLocal } from '../../../helpers/stores'
import { PlainField, ToggleField } from '../../../components/SectionCard'

// С этого тарифа пуш начисляет баллы получателям: pricing_plan считается с нуля, 3 — «Профи», четвёртый
// (то же значение на бэкенде — PushBonusService::PRICING_PLAN)
export const PUSH_BONUS_PLAN = 3
const MAX_DAYS = 365
const DEFAULT_AMOUNT = 100
const DEFAULT_DAYS = 7

// Поля рассылки или шаблона: 0 баллов — без начисления
export interface PushBonusValue {
    bonus_amount?: number | null
    bonus_days?: number | null
}

const VARS = ['{{bonus_amount}}', '{{bonus_date}}']

// Когда сгорят баллы, если отправить сейчас
const combustionDate = (days: any) => dayjs().add(Math.max(Number(days) || 1, 1), 'day').format('DD.MM.YYYY')

const isOn = (value: PushBonusValue) => value.bonus_amount !== undefined && value.bonus_amount !== 0 && `${value.bonus_amount}` !== '0'

// Подстановка {{bonus_amount}} и {{bonus_date}} для превью — как на бэкенде при отправке
export const fillBonusVars = (text: string, value: PushBonusValue) => {
    const amount = isOn(value) ? Number(value.bonus_amount) || 0 : 0

    return text
        .split(VARS[0]).join(`${amount}`)
        .split(VARS[1]).join(amount > 0 ? combustionDate(value.bonus_days) : '')
}

// Для FormData: выключено или не заполнено — 0
export const bonusFormValues = (value: PushBonusValue) => isOn(value)
    ? { bonus_amount: `${Number(value.bonus_amount) || 0}`, bonus_days: `${Number(value.bonus_days) || 0}` }
    : { bonus_amount: '0', bonus_days: '0' }

/**
 * Начисление баллов получателям пуша и срок их сгорания. value — наблюдаемый объект рассылки или шаблона,
 * поля меняются в нём напрямую. template — подсказка о частоте для триггерных пушей
 */
export const PushBonusFields = observer(({ value, template = false }: { value: PushBonusValue, template?: boolean }) => {
    // null — ещё не знаем, включена ли бонусная программа
    const [bonusProgram, setBonusProgram] = useState<boolean | null>(null)

    useEffect(() => {
        openApi('get', 'settings')
            .then(result => setBonusProgram(!!result.data?.project?.bonus))
            .catch(error => console.log(error))
    }, [])

    const locked = storeLocal.get('pricing_plan') < PUSH_BONUS_PLAN
    const on = isOn(value)
    const off = bonusProgram === false

    const toggle = (checked: boolean) => {
        if (checked) {
            value.bonus_amount = Number(value.bonus_amount) > 0 ? value.bonus_amount : DEFAULT_AMOUNT
            value.bonus_days = Number(value.bonus_days) > 0 ? value.bonus_days : DEFAULT_DAYS
        } else {
            value.bonus_amount = 0
            value.bonus_days = 0
        }
    }

    return (
        <div className="pu-bonus">
            <ToggleField
                icon={<GiftOutlined />}
                label={i.t('pushes:bonus.toggle')}
                checked={on}
                onChange={toggle}
                disabled={off && !on}
                plan={PUSH_BONUS_PLAN}
            />

            {locked &&
                <Alert
                    type="warning"
                    showIcon
                    message={i.t('pushes:bonus.planHint')}
                    action={<Button size="small" href="/billing">{i.t('pushes:segment.plans')}</Button>}
                />
            }

            {off &&
                <Alert
                    type="warning"
                    showIcon
                    message={i.t('pushes:bonus.programOff')}
                    action={<Button size="small" href="/settings/bonus_program">{i.t('pushes:bonus.programLink')}</Button>}
                />
            }

            {on &&
                <>
                    <div className="pu-bonus__fields">
                        <PlainField label={i.t('pushes:bonus.amount')}>
                            <InputNumber
                                size="large"
                                style={{ width: '100%' }}
                                min={1}
                                max={1000000}
                                precision={0}
                                value={value.bonus_amount}
                                onChange={(amount) => value.bonus_amount = amount}
                                disabled={locked}
                                required
                            />
                        </PlainField>
                        <PlainField label={i.t('pushes:bonus.days')}>
                            <InputNumber
                                size="large"
                                style={{ width: '100%' }}
                                min={1}
                                max={MAX_DAYS}
                                precision={0}
                                value={value.bonus_days}
                                onChange={(days) => value.bonus_days = days}
                                addonAfter={i.t('pushes:bonus.daysUnit')}
                                disabled={locked}
                                required
                            />
                        </PlainField>
                    </div>

                    <div className="pu-hint">
                        {i.t(template ? 'pushes:bonus.burnsTemplate' : 'pushes:bonus.burns', { date: combustionDate(value.bonus_days) })}
                    </div>

                    <div className="pu-bonus__vars">
                        <span className="pu-hint">{i.t('pushes:bonus.vars')}</span>
                        <span><Tag>{VARS[0]}</Tag> {i.t('pushes:bonus.varAmount')}</span>
                        <span><Tag>{VARS[1]}</Tag> {i.t('pushes:bonus.varDate')}</span>
                    </div>

                    <div className="pu-hint">
                        {i.t('pushes:bonus.recipients')}
                        {' '}
                        {i.t(template ? 'pushes:bonus.onceTemplate' : 'pushes:bonus.oncePush')}
                    </div>
                </>
            }
        </div>
    )
})
